import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { buildKpiAxisPreviewRequest } from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import type { PpaChartResponse } from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import {
    PPA_AXIS_PREVIEW_QUERY_OPTIONS,
    fetchPpaChart,
    getPpaChartQueryKey,
} from 'hooks/ppaChartQuery.helpers';

const KPI_AXIS_PREVIEW_DEBOUNCE_MS = 400;

type PreviewFetchSnapshot = Pick<
    KpiAxisConfiguration,
    'min' | 'max' | 'binningMethod' | 'binCount' | 'breakpoints'
>;

const toFetchSnapshot = (config: KpiAxisConfiguration): PreviewFetchSnapshot => ({
    min: config.min,
    max: config.max,
    binningMethod: config.binningMethod,
    binCount: config.binCount,
    breakpoints: [...config.breakpoints],
});

const hasImmediateFetchChange = (
    previous: PreviewFetchSnapshot | null,
    current: PreviewFetchSnapshot,
) =>
    !previous ||
    previous.binningMethod !== current.binningMethod ||
    previous.breakpoints.join(',') !== current.breakpoints.join(',');

const buildPpaKpiPreviewLabelsFromChart = (response: PpaChartResponse): string[] => {
    const rows = Array.isArray(response) ? response : response?.data ?? [];
    return rows
        .map((column) => column.description?.trim() || column.ex_id?.trim() || '')
        .filter(Boolean);
};

type UsePpaKpiAxisConfigurationPreviewParams = {
    open: boolean;
    chartRequest: PricePackArchitectureRequest | null;
    previewConfig: KpiAxisConfiguration;
    pauseFetch?: boolean;
};

export const usePpaKpiAxisConfigurationPreview = ({
    open,
    chartRequest,
    previewConfig,
    pauseFetch = false,
}: UsePpaKpiAxisConfigurationPreviewParams) => {
    const [debouncedConfig, setDebouncedConfig] = useState(previewConfig);
    const previousSnapshotRef = useRef<PreviewFetchSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            previousSnapshotRef.current = null;
            return undefined;
        }

        const currentSnapshot = toFetchSnapshot(previewConfig);
        const fetchDelay = hasImmediateFetchChange(previousSnapshotRef.current, currentSnapshot)
            ? 0
            : KPI_AXIS_PREVIEW_DEBOUNCE_MS;
        previousSnapshotRef.current = currentSnapshot;

        const timer = window.setTimeout(() => {
            setDebouncedConfig(previewConfig);
        }, fetchDelay);

        return () => {
            window.clearTimeout(timer);
        };
    }, [open, previewConfig]);

    const previewRequest = useMemo(() => {
        if (!chartRequest || !open) {
            return null;
        }
        return buildKpiAxisPreviewRequest(chartRequest, debouncedConfig);
    }, [chartRequest, debouncedConfig, open]);

    const hasValidRange = debouncedConfig.min < debouncedConfig.max;
    const hasValidBinCount =
        debouncedConfig.binningMethod === 'uniform'
            ? debouncedConfig.binCount > 0
            : debouncedConfig.breakpoints.length > 0;
    const queryEnabled = open && Boolean(previewRequest) && hasValidRange && hasValidBinCount && !pauseFetch;

    const { data, isPending, isFetching } = useQuery<string[]>({
        queryKey: previewRequest ? getPpaChartQueryKey(previewRequest) : ['ppa-kpi-axis-preview', 'disabled'],
        queryFn: async ({ signal }) => {
            const response = await fetchPpaChart(previewRequest!, signal);
            return buildPpaKpiPreviewLabelsFromChart(response);
        },
        enabled: queryEnabled,
        placeholderData: keepPreviousData,
        ...PPA_AXIS_PREVIEW_QUERY_OPTIONS,
    });

    const isConfigDebouncing =
        open &&
        JSON.stringify(toFetchSnapshot(previewConfig)) !== JSON.stringify(toFetchSnapshot(debouncedConfig));

    const isLoading = queryEnabled && (isPending || isFetching || isConfigDebouncing);

    return {
        previewLabels: open ? (data ?? []) : [],
        isLoading,
    };
};
